import {AnalyzerType, HazardMapType, useGetAddressInfoQuery, ZyusetuFieldKeyFragmentFragment,} from "@/graphql/graphql";
import {useMemo} from "react";
import {useCrossSectionFields} from "@/hooks/use-cross-section-fields";
import {DisportalDataViewer} from "@/components/pages/Zyusetu/SpecialFunctions/DisportalDataViewer.tsx";
import {FormSchema, UpdateFieldOptions} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {Link} from "react-router-dom";
import {Box, Button, Chip} from "@mui/material";

interface ZyusetuEditorSpecialFunctionsProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema[string],
    options?: UpdateFieldOptions
  ) => Promise<void>;
}

export function ZyusetuEditorSpecialFunctions({
                                                fieldKey,
                                                updateField,
                                              }: ZyusetuEditorSpecialFunctionsProps) {
  const crossFields = useCrossSectionFields();
  
  const form = useMemo(() => {
    switch (fieldKey?.keyStyle.specialFunctionName) {
      case "isInHightideArea":
      case "isInNaisuiArea":
      case "isInFloodArea":
      case "isInTsunamiDisasterAlertAreas":
      case "isInSedimentDisasterAlertAreas":
      case "isInDisasterProneDevelopedResidentialArea":
        return (
          crossFields.address && (
            <ZyusetuEditorSpecialAddressFunctions
              address={crossFields.address}
              fieldKey={fieldKey}
              updateField={updateField}
            />
          )
        );
    }
  }, [crossFields.address, fieldKey, updateField]);
  
  return <>{form}</>;
}

function ZyusetuEditorSpecialAddressFunctions({
                                                address,
                                                fieldKey,
                                                updateField,
                                              }: {
  address: string;
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema[string],
    options?: UpdateFieldOptions
  ) => Promise<void>;
}) {
  const {data: addressInfo} = useGetAddressInfoQuery({
    address,
  }, {
    retry: false
  });
  
  const form = useMemo(() => {
    if (!addressInfo?.getAddressInfo) {
      return;
    }
    switch (fieldKey?.keyStyle.specialFunctionName) {
      case "isInDisasterProneDevelopedResidentialArea": {
        
        return <Chip tabIndex={-1}
                     label={addressInfo?.getAddressInfo?.isInDisasteProneDevelopedResidentialArea ? "該当する可能性があります" : "該当なし"}
                     color={
                       addressInfo?.getAddressInfo?.isInDisasteProneDevelopedResidentialArea ? "error" : "default"
                     }/>;
      }
      case "isInSedimentDisasterAlertAreas": {
        //   土砂災害
        // https://disaportal.gsi.go.jp/hazardmap/maps/index.html?ll=35.67323,139.902434&z=15&base=pale&ls=dosha_keikai_jisuberi%2C0.8%7Cdosha_keikai_dosekiryu%2C0.8%7Cdosha_keikai_kyukeisha%2C0.8&disp=111&vs=c1j0l0u0t0h0z0
        return (
          <DisportalDataViewer
            kukaku={"土砂災害区域"}
            fieldKey={fieldKey}
            address={addressInfo.getAddressInfo}
            updateField={updateField}
            baseLink={
              "https://disaportaldata.gsi.go.jp/raster/05_dosekiryukeikaikuiki"
            }
            ls={
              "dosha_keikai_jisuberi%2C0.8%7Cdosha_keikai_dosekiryu%2C0.8%7Cdosha_keikai_kyukeisha%2C0.8&disp=111&vs=c1j0l0u0t0h0z0"
            }
          />
        );
      }
      case "isInTsunamiDisasterAlertAreas": {
        //   津波
        // https://disaportal.gsi.go.jp/hazardmap/maps/index.html?ll=35.67323,139.902434&z=15&base=pale&ls=tsunamishinsui_raster%2C0.8%7Cdisaster5&disp=10&vs=c1j0l0u0t0h0z0
        return (
          <DisportalDataViewer
            kukaku={"津波警報区域"}
            baseLink={
              "https://disaportaldata.gsi.go.jp/raster/04_tsunami_newlegend_data"
            }
            address={addressInfo.getAddressInfo}
            fieldKey={fieldKey}
            updateField={updateField}
            ls={
              "tsunamishinsui_raster%2C0.8%7Cdisaster5&disp=10&vs=c1j0l0u0t0h0z0"
            }
          />
        );
      }
      case "isInFloodArea": {
        
        // 洪水区域
        //
        // https://disaportal.gsi.go.jp/hazardmap/maps/index.html?ll=35.672358,139.798&z=16&base=pale&ls=flood_list_l2%2C0.75&disp=1&vs=c1j0l0u0t0h0z0
        const map = addressInfo.getAddressInfo.hazardMapLinks?.find((link) => link.type === HazardMapType.FloodArea)
        return (
          <>
            <DisportalDataViewer
              kukaku={"洪水区域"}
              baseLink={
                "https://disaportaldata.gsi.go.jp/raster/01_flood_l2_shinsuishin_data"
              }
              address={addressInfo.getAddressInfo}
              fieldKey={fieldKey}
              updateField={updateField}
              ls={"flood_list_l2%2C0.75&disp=1&vs=c1j0l0u0t0h0z0"}
            />
            {map && <Box> <Button component={Link} to={map.url} target={'_blank'} tabIndex={-1}
                                  sx={{textDecoration: 'underline'}}>ハザードマップはこちら</Button></Box>}
          </>
        );
      }
      // 高潮区域
      //   https://disaportal.gsi.go.jp/hazardmap/maps/index.html?ll=35.672358,139.798&z=16&base=pale&ls=takashio_sinsuisin_raster%2C0.8&disp=1&vs=c1j0l0u0t0h0z0
      case "isInHightideArea": {
        const map = addressInfo.getAddressInfo.hazardMapLinks?.find((link) => link.type === HazardMapType.HightideArea)
        return (
          <>
            <DisportalDataViewer
              kukaku={"高潮区域"}
              baseLink={
                "https://disaportaldata.gsi.go.jp/raster/03_hightide_l2_shinsuishin_data"
              }
              address={addressInfo?.getAddressInfo}
              fieldKey={fieldKey}
              updateField={updateField}
              ls={"takashio_sinsuisin_raster%2C0.8&disp=1&vs=c1j0l0u0t0h0z0"}
            />
            {map && <Box><Button component={Link} to={map.url} target={'_blank'} tabIndex={-1}
                                 sx={{textDecoration: 'underline'}}>ハザードマップはこちら</Button></Box>}
          </>
        );
      }
      case "isInNaisuiArea": {
        const map = addressInfo.getAddressInfo.hazardMapLinks?.find((link) => link.type === HazardMapType.NaisuiArea)
        return (
          // 雨水出水
          // https://disaportal.gsi.go.jp/hazardmap/maps/index.html?ll=35.672358,139.798&z=16&base=pale&ls=naisui_raster%2C0.8&disp=1&vs=c1j0l0u0t0h0z0
          <>
            
            <DisportalDataViewer
              kukaku={"雨水出水区域"}
              fieldKey={fieldKey}
              address={addressInfo.getAddressInfo}
              updateField={updateField}
              baseLink={"https://disaportaldata.gsi.go.jp/raster/02_naisui_data"}
              ls={"naisui_raster%2C0.8&disp=1&vs=c1j0l0u0t0h0z0"}
            />
            {map && <Box><Button component={Link} to={map.url} target={'_blank'} tabIndex={-1}
                                 sx={{textDecoration: 'underline'}}>ハザードマップはこちら</Button></Box>}
          </>
        
        );
      }
      case "floorAreaCalculator": {
        const data = fieldKey.analyzedPayloads?.find((pay) => pay.type === AnalyzerType.DocumentAi)
        if (!data) {
          return <></>;
        }
        
      }
    }
  }, [addressInfo?.getAddressInfo, fieldKey, updateField]);
  
  return <>{form}</>;
}
